// The square is the sign-off (docs/brand/BRAND.md, "Visual concept"). Each production step is a square:
// filled with a frame of the work once signed off, an outline with a slow pulse while it waits for sign-off,
// a neutral outline while open, and dashed while gated; a step the production skips is a faint hairline. Corners
// are nearly sharp. The outline of a waiting
// square and the fill of a signed-off one take the step's department colour when there is one. An extra
// department (voices, music, sound, edit) is a narrower square that sits close to the core one it belongs to.
import { type CSSProperties, useState } from "react";
import { DEPARTMENT_LABEL, type Department, deptColor, isExtra, parentOf } from "../departments.ts";

/**
 * The four BRAND states, plus "progress" (work started, in the department's colour but without the pulse) and
 * "skipped" (the production does not run the step).
 */
export type SquareState = "signed" | "waiting" | "progress" | "open" | "gated" | "skipped";

export const SQUARE_LABEL: Record<SquareState, string> = {
  signed: "Signed off",
  waiting: "Waiting for sign-off",
  progress: "In progress",
  open: "Open",
  gated: "Gated",
  skipped: "Skipped",
};

export function StatusSquare({
  state,
  dept,
  frame,
  size = 10,
  narrow = false,
  label,
}: {
  state: SquareState;
  dept?: Department | null;
  /** a still of the signed-off work; it fills the square */
  frame?: string | null;
  size?: number;
  /** an extra department's square: as tall, narrower */
  narrow?: boolean;
  /** spoken label; without one the square is decoration next to a word */
  label?: string;
}) {
  const style = {
    "--sq-size": `${size}px`,
    ...(narrow ? { "--sq-w": `${narrowWidth(size)}px` } : {}),
    ...(dept ? { "--sq": deptColor(dept) } : {}),
  } as CSSProperties;
  // The moment a square turns signed off while on screen it snaps full (motion.css); one already signed when it
  // mounts just sits there.
  const [prev, setPrev] = useState(state);
  const [snap, setSnap] = useState(false);
  if (prev !== state) {
    setPrev(state);
    setSnap(state === "signed");
  }
  const cls = `sq sq-${state}${snap ? " sq-snap" : ""}`;
  const fill = state === "signed" && frame ? <img src={frame} alt="" draggable={false} /> : null;
  return label ? (
    <span className={cls} style={style} role="img" aria-label={label}>
      {fill}
    </span>
  ) : (
    <span className={cls} style={style} aria-hidden="true">
      {fill}
    </span>
  );
}

/** The width of an extra department's square: about 60% of a core one, never under 3px. */
export const narrowWidth = (size: number): number => Math.max(3, Math.round(size * 0.6));

/**
 * Where an extra's square sits against its family: "after" its parent (voices after casting), or "before" it
 * (the edit, just before the final). Null for a core step, or an extra whose parent is not next to it.
 */
export function familySide(steps: readonly { dept: Department }[], i: number): "after" | "before" | null {
  const d = steps[i]?.dept;
  if (!d || !isExtra(d)) return null;
  const p = parentOf(d);
  // an extra right after its parent, or after a sibling that is (two extras of one parent)
  for (let j = i - 1; j >= 0; j--) {
    const prev = steps[j]!.dept;
    if (prev === p) return "after";
    if (!isExtra(prev) || parentOf(prev) !== p) break;
  }
  if (steps[i + 1]?.dept === p) return "before";
  return null;
}

export interface Step {
  dept: Department;
  state: SquareState;
  frame?: string | null;
}

/** A production's steps as a row of squares, in production order, the way the logo and the end credits show it. */
export function SquareStrip({
  steps,
  size = 14,
  label = "Production steps",
}: {
  steps: readonly (Step & { label?: string })[];
  size?: number;
  label?: string;
}) {
  return (
    <ul
      className="sq-strip"
      aria-label={label}
      style={{ "--sq-gap": `${Math.max(2, Math.round(size / 5))}px` } as CSSProperties}
    >
      {steps.map((s, i) => (
        <li key={s.dept} className={`sq-step${familyClass(familySide(steps, i))}`}>
          <StatusSquare
            state={s.state}
            dept={s.dept}
            frame={s.frame}
            size={size}
            narrow={isExtra(s.dept)}
            label={`${DEPARTMENT_LABEL[s.dept]}: ${s.label ?? SQUARE_LABEL[s.state]}`}
          />
        </li>
      ))}
    </ul>
  );
}

const familyClass = (side: "after" | "before" | null) =>
  side === "after" ? " sq-step-after" : side === "before" ? " sq-step-before" : "";

/** Which department something belongs to: a small square in the department's colour and its name. */
export function DeptTag({ dept }: { dept: Department }) {
  return (
    <span className="dept-tag">
      <span className="dept-tag-sq" style={{ background: deptColor(dept) }} aria-hidden="true" />
      {DEPARTMENT_LABEL[dept]}
    </span>
  );
}
